/**
 * Admin — Affiliates section.
 *
 * Route: /admin/affiliates
 *
 * Read-only summary + affiliate table with per-row actions:
 *   approve · reject · suspend · disable · mark payout paid
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function dollars(cents) {
  if (typeof cents !== "number") return "$0.00";
  const sign = cents < 0 ? "-" : "";
  const v = Math.abs(cents);
  return `${sign}$${Math.floor(v / 100)}.${String(v % 100).padStart(2, "0")}`;
}

// Status filter tabs. Default view shows ALL affiliates so admins can
// see the full program at a glance. Tabs act as optional filters only —
// clicking one narrows the table to that status; clicking "All" restores
// the full list.
const STATUS_TABS = [
  { id: "all",       label: "All" },
  { id: "pending",   label: "Pending" },
  { id: "active",    label: "Active" },
  { id: "suspended", label: "Suspended" },
  { id: "disabled",  label: "Disabled" },
  { id: "rejected",  label: "Rejected" },
];

export default function AdminAffiliatesPage() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const reload = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/affiliates/summary"),
        api.get("/admin/affiliates/list"),
      ]);
      setSummary(s.data);
      setRows(l.data?.affiliates || []);
    } catch (e) {
      toast.error("Failed to load affiliate data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const setStatus = async (aff, status) => {
    if (!window.confirm(`Set affiliate to ${status}?`)) return;
    try {
      await api.post(`/admin/affiliates/${aff.affiliate_id}/status`, { status });
      toast.success(`Marked ${status}`);
      reload();
    } catch {
      toast.error("Could not update status");
    }
  };

  const payout = async (aff) => {
    const method = window.prompt("Payment method (e.g. PayPal, Wise)");
    if (!method) return;
    const ref = window.prompt("Payment reference / transaction ID (optional)") || "";
    if (!window.confirm(`Mark payout PAID for ${aff.affiliate_code}?`)) return;
    try {
      const r = await api.post(`/admin/affiliates/${aff.affiliate_id}/pay`, {
        payment_method: method,
        payment_reference: ref,
      });
      toast.success(`Paid ${dollars(r.data?.amount_cents || 0)}`);
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const reason = d?.reason || "error";
      toast.error(`Payout blocked: ${reason}`);
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-8" data-testid="admin-affiliates-page">
      <div>
        <h1 className="font-display text-3xl">Affiliate Program</h1>
        <p className="text-sm text-[color:var(--text-2)] mt-1">
          Program observability, approvals, and manual payouts.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="admin-affiliates-summary">
          <Metric label="Total affiliates" value={summary.total_affiliates} tid="sum-total" />
          <Metric label="Pending applications" value={summary.pending_applications} tid="sum-pending-apps" />
          <Metric label="Active affiliates" value={summary.active_affiliates} tid="sum-active" />
          <Metric label="Referral clicks" value={summary.clicks} tid="sum-clicks" />
          <Metric label="Attributed signups" value={summary.signups} tid="sum-signups" />
          <Metric label="Paid customers" value={summary.paid_customers} tid="sum-paid-customers" />
          <Metric label="Attributed revenue" value={dollars(summary.attributed_revenue_cents)} tid="sum-revenue" />
          <Metric
            label="Affiliate Liability"
            value={summary.affiliate_liability_display}
            emphasize
            tid="sum-liability"
          />
          <Metric label="Pending commissions" value={dollars(summary.pending_commission_cents)} tid="sum-pending-comm" />
          <Metric label="Approved unpaid" value={dollars(summary.approved_unpaid_commission_cents)} tid="sum-approved-unpaid" />
          <Metric label="Paid commissions" value={dollars(summary.paid_commission_cents)} tid="sum-paid" />
          <Metric label="Reversed" value={dollars(summary.reversed_commission_cents)} tid="sum-reversed" />
        </div>
      )}

      {summary && (
        <p className="text-xs text-[color:var(--text-2)]" data-testid="admin-liability-reminder">
          Reminder: keep at least <strong>{summary.affiliate_liability_display}</strong>{" "}
          reserved in your separate affiliate reserve account.
        </p>
      )}

      <section>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h2 className="font-display text-xl">Affiliates</h2>
          <div className="flex items-center gap-1 flex-wrap" data-testid="admin-affiliate-status-tabs">
            {STATUS_TABS.map((t) => {
              const count = t.id === "all"
                ? rows.length
                : rows.filter((r) => r.status === t.id).length;
              const isActive = statusFilter === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setStatusFilter(t.id)}
                  data-testid={`admin-affiliate-tab-${t.id}`}
                  className={
                    "px-3 py-1.5 rounded-md text-xs font-medium border transition-colors " +
                    (isActive
                      ? "bg-[color:var(--brand-primary,#1BA89F)] text-black border-transparent"
                      : "border-[color:var(--border)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                  }
                >
                  {t.label} <span className="opacity-70 tabular-nums">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
        {(() => {
          const filteredRows = statusFilter === "all"
            ? rows
            : rows.filter((r) => r.status === statusFilter);
          return (
        <div className="overflow-x-auto rounded-lg border border-[color:var(--border)] bg-[color:var(--bg-1)]">
          <table className="min-w-full text-sm" data-testid="admin-affiliates-table">
            <thead className="text-xs uppercase tracking-widest text-[color:var(--text-2)]">
              <tr>
                <th className="text-left px-3 py-3">Affiliate</th>
                <th className="text-left px-3 py-3">Code</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Applied</th>
                <th className="text-right px-3 py-3">Clicks</th>
                <th className="text-right px-3 py-3">Signups</th>
                <th className="text-right px-3 py-3">Customers</th>
                <th className="text-right px-3 py-3">Revenue</th>
                <th className="text-right px-3 py-3">Pending</th>
                <th className="text-right px-3 py-3">Available</th>
                <th className="text-right px-3 py-3">Paid</th>
                <th className="text-right px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="px-4 py-6 text-[color:var(--text-2)]">Loading…</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={12} className="px-4 py-6 text-[color:var(--text-2)]" data-testid="admin-affiliates-empty">
                  No affiliates with status &quot;{statusFilter}&quot;.
                </td></tr>
              ) : (
                filteredRows.map((r) => (
                  <tr
                    key={r.affiliate_id}
                    className={
                      "border-t border-[color:var(--border)] " +
                      (r.status === "pending" ? "bg-[color:rgba(255,190,110,0.06)]" : "")
                    }
                    data-testid="admin-affiliate-row"
                  >
                    <td className="px-3 py-3">
                      <div className="font-medium">{r.application_name || "—"}</div>
                      <div className="text-xs text-[color:var(--text-2)]">{r.application_email || "—"}</div>
                    </td>
                    <td className="px-3 py-3"><code>{r.affiliate_code}</code></td>
                    <td className="px-3 py-3">
                      <span
                        className={
                          "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border " +
                          (r.status === "pending"
                            ? "border-[color:rgba(255,190,110,0.45)] bg-[color:rgba(255,190,110,0.12)] text-[color:rgb(255,190,110)]"
                            : r.status === "active"
                            ? "border-[color:rgba(27,168,159,0.4)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary,#1BA89F)]"
                            : "border-[color:var(--border)] text-[color:var(--text-2)]")
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-[color:var(--text-2)] tabular-nums">
                      {r.created_at ? String(r.created_at).slice(0, 10) : "—"}
                    </td>
                    <td className="px-3 py-3 text-right">{r.clicks}</td>
                    <td className="px-3 py-3 text-right">{r.signups}</td>
                    <td className="px-3 py-3 text-right">{r.paying_customers}</td>
                    <td className="px-3 py-3 text-right">{dollars(r.referred_revenue_cents)}</td>
                    <td className="px-3 py-3 text-right">{dollars(r.pending_cents)}</td>
                    <td className="px-3 py-3 text-right">{dollars(r.available_cents)}</td>
                    <td className="px-3 py-3 text-right">{dollars(r.paid_cents)}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex flex-wrap gap-1 justify-end">
                        {r.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => setStatus(r, "active")} data-testid="admin-approve-btn">Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => setStatus(r, "rejected")} data-testid="admin-reject-btn">Reject</Button>
                          </>
                        )}
                        {r.status === "active" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setStatus(r, "suspended")} data-testid="admin-suspend-btn">Suspend</Button>
                            <Button size="sm" variant="outline" onClick={() => setStatus(r, "disabled")} data-testid="admin-disable-btn">Disable</Button>
                            <Button size="sm" onClick={() => payout(r)} data-testid="admin-pay-btn">Mark payout paid</Button>
                          </>
                        )}
                        {(r.status === "suspended" || r.status === "disabled") && (
                          <Button size="sm" onClick={() => setStatus(r, "active")} data-testid="admin-reactivate-btn">Reactivate</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          );
        })()}
      </section>
    </div>
  );
}

function Metric({ label, value, emphasize, tid }) {
  return (
    <div
      className={`rounded-lg border p-4 ${emphasize ? "border-[color:var(--accent)] bg-[color:var(--accent-bg,var(--bg-1))]" : "border-[color:var(--border)] bg-[color:var(--bg-1)]"}`}
      data-testid={tid}
    >
      <p className="text-xs uppercase tracking-widest text-[color:var(--text-2)]">{label}</p>
      <p className="mt-2 font-display text-xl">{value}</p>
    </div>
  );
}
